import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BombiixModal } from '../BombiixModal';
import { NewCheckbox } from '../PIM/common';
import './ProductComponentModal.css';

export interface ProductComponentList {
  bomColorApproval: boolean;
  bomApproval: boolean;
  careReport: boolean;
  testReport: boolean;
  sample: boolean;
  documents: boolean;
  selectAll: boolean;
}

export interface ProductComponentModalProps {
  show: boolean;
  handleClose: () => void;
  onSaveClick: (componentList: ProductComponentList) => void;
}

const COMPONENT_OPTIONS: Array<{ name: keyof ProductComponentList; labelKey: string }> = [
  { name: 'bomColorApproval', labelKey: 'PRODUCT_COMPONEBT_MODAL.COLOR_STATUS' },
  { name: 'bomApproval', labelKey: 'PRODUCT_COMPONEBT_MODAL.QUALITY_STATUS' },
  { name: 'careReport', labelKey: 'PRODUCT_COMPONEBT_MODAL.CARE_LABEL_STATUS' },
  { name: 'testReport', labelKey: 'PRODUCT_COMPONEBT_MODAL.TESTING_STATUS' },
  { name: 'sample', labelKey: 'PRODUCT_COMPONEBT_MODAL.SAMPLE' },
];

/**
 * The removeApproval payload for a duplicate that carries nothing over. Used when the original
 * shares no supplier and no colourway with the copy, so there is no status the copy could inherit
 * and the checklist is never shown.
 */
export const NO_CARRIED_COMPONENTS: ProductComponentList = {
  bomColorApproval: false,
  bomApproval: false,
  careReport: false,
  testReport: false,
  sample: false,
  documents: false,
  selectAll: false,
};

/**
 * ProductComponentModal — component checklist shown when duplicating a product.
 * Lets the user pick which statuses of the original product carry over to the
 * duplicate; the selection is returned as the removeApproval payload. Rendered
 * in Bombiix master-design chrome via BombiixModal + NewCheckbox.
 */
function ProductComponentModal({ show, handleClose, onSaveClick }: ProductComponentModalProps) {
  const { t } = useTranslation('common');

  const [componentList, setComponentList] = useState<ProductComponentList>({
    bomColorApproval: true,
    bomApproval: true,
    careReport: true,
    testReport: true,
    sample: true,
    documents: true,
    selectAll: true,
  });

  const handleComponentCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setComponentList((prev) => ({ ...prev, [name]: checked, selectAll: checked }));
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setComponentList({
      bomColorApproval: checked,
      bomApproval: checked,
      careReport: checked,
      testReport: checked,
      sample: checked,
      documents: checked,
      selectAll: checked,
    });
  };

  return (
    <BombiixModal
      show={show}
      title={t('PRODUCT_COMPONEBT_MODAL.ORIGINAL_PRODUCT')}
      onClose={handleClose}
      onSave={() => onSaveClick(componentList)}
      size="lg"
      className="bombiix-modal--product-component"
    >
      <div className="product-component-list">
        <NewCheckbox id="selectAll" name="selectAll" label={t('COMMON_LABEL.SELECT_ALL')} checked={componentList.selectAll} onChange={handleSelectAll} />
        {COMPONENT_OPTIONS.map((option) => (
          <NewCheckbox
            key={option.name}
            id={option.name}
            name={option.name}
            label={t(option.labelKey)}
            checked={!!componentList[option.name]}
            onChange={handleComponentCheck}
          />
        ))}
      </div>
    </BombiixModal>
  );
}

export default ProductComponentModal;
